/**
 * « Nouveau message » : un seul formulaire pour écrire à des collègues, à des
 * élèves, ou aux deux. Il annonce ce qui sera créé avant de valider, car les
 * deux messageries restent distinctes côté serveur :
 * - des collègues seulement : conversation de l'équipe, comme auparavant ;
 * - un élève seul : échange à deux avec lui ;
 * - un élève et d'autres personnes : groupe avec élèves.
 */
import { useRef, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import type { Directory } from '../api/messaging.types';
import {
  studentMessagingApi,
  studentMessagingError,
  type StudentContact,
  type StudentMessagingContacts,
} from '../api/studentMessaging';
import { planNewMessage } from '../lib/unifiedMessaging';
import { OFFLINE } from './StudentMessageParts';
import { StudentPicker } from './StudentMessageStart';

const ROLE_LABELS: Record<string, string> = {
  owner: 'Propriétaire',
  manager: 'Responsable',
  agent: 'Secrétariat',
  instructor: 'Moniteur',
  read_only: 'Lecture seule',
};

/** Les choix en cours : gardés par l'écran, ils survivent au repli du formulaire. */
export type NewMessageDraft = {
  /** Collègues cochés dans l'annuaire de la messagerie de l'équipe. */
  recipients: string[];
  title: string;
  students: StudentContact[];
};

export const EMPTY_DRAFT: NewMessageDraft = {
  recipients: [],
  title: '',
  students: [],
};

export type TeamConversationRequest = {
  type: 'DIRECT' | 'GROUP';
  title: string;
  participants: string[];
};

const count = (n: number, noun: string) => `${n} ${noun}${n > 1 ? 's' : ''}`;

export default function NewMessageForm({
  draft,
  onDraft,
  me,
  directory,
  refreshing,
  onRefresh,
  busy,
  studentAccess,
  onCreateTeam,
  onTeamCreated,
  onStudentConversation,
}: {
  draft: NewMessageDraft;
  onDraft: (draft: NewMessageDraft) => void;
  me?: string;
  /** Annuaire de la messagerie de l'équipe ; absent tant qu'il n'est pas disponible. */
  directory?: Directory;
  refreshing: boolean;
  onRefresh: () => void;
  /** Une commande de la messagerie de l'équipe est en cours. */
  busy: boolean;
  /** Droits et équipe de la messagerie des élèves ; absent si l'on ne peut pas leur écrire. */
  studentAccess?: StudentMessagingContacts;
  /** Crée la conversation de l'équipe ; `true` une fois confirmée. */
  onCreateTeam: (conversation: TeamConversationRequest) => Promise<boolean>;
  onTeamCreated: () => void;
  /** L'échange ou le groupe avec élèves existe : l'écran l'ouvre. */
  onStudentConversation: (conversationId: string) => void;
}) {
  const { recipients, title } = draft;
  const students = studentAccess ? draft.students : [];
  // Un nouvel essai de la même demande de groupe reprend son identifiant : pas de doublon.
  const pending = useRef({ signature: '', requestId: '' });

  const nameOf = (id: string) => {
    const person = directory?.items.find((u) => u.id === id);
    return person ? `${person.firstName} ${person.lastName}` : 'un collègue';
  };
  // Un groupe avec élèves ne réunit que l'équipe de leur école.
  const schoolStaff = (studentAccess?.staff ?? []).map((person) => person.id);
  const plan = planNewMessage({
    colleagues: recipients.length,
    students: students.length,
    outsiders: students.length
      ? recipients.filter((id) => !schoolStaff.includes(id)).map(nameOf)
      : [],
    canOpenDirect: !!studentAccess?.canOpenDirect,
    canCreateGroup: !!studentAccess?.canCreateGroup,
  });
  const withStudent =
    plan.kind === 'student-direct' || plan.kind === 'student-group';

  const start = useMutation({
    mutationFn: (kind: 'student-direct' | 'student-group') => {
      if (kind === 'student-direct')
        return studentMessagingApi.openDirect(students[0].id);
      const group = {
        title: title.trim(),
        staffIds: recipients,
        candidateIds: students.map((s) => s.id),
      };
      const signature = JSON.stringify(group);
      if (pending.current.signature !== signature)
        pending.current = { signature, requestId: crypto.randomUUID() };
      return studentMessagingApi.createGroup({
        requestId: pending.current.requestId,
        ...group,
      });
    },
    onSuccess: ({ id }) => {
      pending.current = { signature: '', requestId: '' };
      onDraft(EMPTY_DRAFT);
      onStudentConversation(id);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (plan.kind === 'student-direct' || plan.kind === 'student-group') {
      if (!start.isPending) start.mutate(plan.kind);
      return;
    }
    if (plan.kind !== 'team') return;
    void onCreateTeam({
      type: plan.type,
      title: title || 'Échange direct',
      participants: recipients,
    }).then((ok) => {
      if (ok) {
        onTeamCreated();
        onDraft(EMPTY_DRAFT);
      }
    });
  }

  const untitledGroup = plan.kind === 'student-group' && !title.trim();
  let outcome: string;
  if (plan.kind === 'none')
    outcome = studentAccess
      ? 'Choisissez au moins un collègue ou un élève.'
      : 'Choisissez au moins un destinataire.';
  else if (plan.kind === 'blocked')
    outcome = `Création impossible. ${plan.reason}`;
  else if (plan.kind === 'team')
    outcome =
      plan.type === 'DIRECT'
        ? `Ce qui sera créé : une conversation interne à deux avec ${nameOf(recipients[0])}, réservée à l’équipe.`
        : `Ce qui sera créé : un groupe interne de ${count(recipients.length, 'collègue')}, réservé à l’équipe.`;
  else if (plan.kind === 'student-direct')
    outcome = `Ce qui sera créé : un échange à deux avec ${students[0].name}, élève. Texte seul.`;
  else
    outcome = `Ce qui sera créé : un groupe avec élèves (${count(students.length, 'élève')}, ${
      recipients.length ? count(recipients.length, 'collègue') : 'aucun collègue'
    } et vous). Les élèves liront tous les messages ; texte seul.${
      untitledGroup ? ' Donnez un nom au groupe pour le créer.' : ''
    }`;

  return (
    <form className="messaging-form messaging-new" onSubmit={submit}>
      {students.length ? (
        <p>
          Un élève participe : la conversation est créée dans la messagerie
          des élèves. Sa composition est fixée à la création.
        </p>
      ) : (
        <p>
          Les DM et groupes restent réservés aux participants. Les personnes
          ajoutées voient tout l’historique.
        </p>
      )}
      <label>
        Nom du groupe
        <input
          className="messaging-field"
          required={
            plan.kind === 'student-group' ||
            (!students.length && recipients.length > 1)
          }
          value={title}
          onChange={(e) => onDraft({ ...draft, title: e.target.value })}
          maxLength={120}
        />
      </label>
      <fieldset>
        <legend>Collègues</legend>
        {directory && !directory.items.some((u) => u.id !== me) && (
          <p>
            Aucun autre compte actif disponible dans votre organisation. Une
            fiche moniteur seule ne donne pas accès à la messagerie.
          </p>
        )}
        {directory?.items
          .filter((u) => u.id !== me)
          .map((u) => (
            <label className="messaging-check" key={u.id}>
              <input
                type="checkbox"
                checked={recipients.includes(u.id)}
                onChange={(e) =>
                  onDraft({
                    ...draft,
                    recipients: e.target.checked
                      ? [...recipients, u.id]
                      : recipients.filter((id) => id !== u.id),
                  })
                }
              />
              {u.firstName} {u.lastName}
              <small>
                {ROLE_LABELS[u.role ?? ''] ?? ''}
                {u.schoolId &&
                directory.schools?.find((s) => s.id === u.schoolId)?.name
                  ? ` · ${directory.schools.find((s) => s.id === u.schoolId)!.name}`
                  : ''}
              </small>
            </label>
          ))}
        <div>
          <button
            type="button"
            className="messaging-ghost"
            disabled={refreshing}
            onClick={onRefresh}
          >
            <RefreshCw size={14} aria-hidden="true" />
            Actualiser les destinataires
          </button>
        </div>
      </fieldset>
      {studentAccess && (
        <fieldset>
          <legend>Élèves</legend>
          <StudentPicker
            students={draft.students}
            onChange={(picked) => onDraft({ ...draft, students: picked })}
          />
        </fieldset>
      )}
      <p
        role="status"
        className={`messaging-outcome${
          plan.kind === 'blocked'
            ? ' messaging-outcome--blocked'
            : withStudent
              ? ' messaging-outcome--student'
              : ''
        }`}
      >
        {outcome}
      </p>
      {start.isError && (
        <p role="alert" className="messaging-alert">
          {studentMessagingError(start.error, OFFLINE)}
        </p>
      )}
      <div className="messaging-form-actions">
        <button
          className="messaging-button"
          disabled={
            busy ||
            start.isPending ||
            plan.kind === 'none' ||
            plan.kind === 'blocked' ||
            untitledGroup
          }
        >
          {plan.kind === 'student-direct'
            ? 'Ouvrir l’échange avec l’élève'
            : plan.kind === 'student-group'
              ? 'Créer le groupe avec élèves'
              : 'Créer la conversation'}
        </button>
      </div>
    </form>
  );
}
